// frontend/src/hooks/useUserPreferences.js
import { useState, useCallback, useEffect } from 'react';
// Nur noch die /profile Endpunkte importieren
import { fetchUserProfileAPI, updateUserProfileAPI } from '../services/api';
import { useAuth } from './useAuth';

export const useUserPreferences = () => {
  const [columns, setColumns] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [preferredView, setPreferredView] = useState('table');
  // Nur noch diese Ladezustände
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [error, setError] = useState(null);
  const { handleApiError } = useAuth();

  // allColumnsConfig bleibt gleich
  const allColumnsConfig = [
      { id: 'thumbnail', label: 'Cover', visible: true },
      { id: 'title', label: 'Titel', visible: true },
      { id: 'authors', label: 'Autoren', visible: true },
      { id: 'isbn', label: 'ISBN', visible: true },
      { id: 'location', label: 'Lagerort', visible: true },
      { id: 'publishedDate', label: 'Erschienen', visible: false },
      { id: 'genre', label: 'Genre', visible: false },
      { id: 'pageCount', label: 'Seiten', visible: false },
      { id: 'description', label: 'Beschreibung', visible: false },
  ];

  // Lädt das gesamte Profil (inkl. Spalten und Ansicht)
  const fetchProfile = useCallback(async () => {
      setIsLoadingProfile(true);
      setError(null);
      try {
          const data = await fetchUserProfileAPI();
          setUserProfile(data);
          // Setze Spalten aus dem Profil
          if (data?.tableColumns) {
              const columnsWithLabels = data.tableColumns
                .map(colId => allColumnsConfig.find(col => col.id === colId))
                .filter(col => col); // Nur gültige Spalten behalten
              setColumns(columnsWithLabels);
          } else {
              // Fallback: Standardspalten, wenn nichts gespeichert ist
              setColumns(allColumnsConfig.filter(c => c.visible));
          }
          // Setze Ansicht aus dem Profil
          if (data?.preferredView) {
              setPreferredView(data.preferredView);
          } else {
              setPreferredView('table'); // Fallback
          }
      } catch (err) {
          handleApiError(err);
          setError(err.message);
          // Fallback-Werte bei Fehler
          setColumns(allColumnsConfig.filter(c => c.visible));
          setPreferredView('table');
      } finally {
          setIsLoadingProfile(false);
      }
  // allColumnsConfig ist konstant und muss nicht in die Abhängigkeiten
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleApiError]);

  // Funktion zum Speichern von Präferenzen (Spalten ODER Ansicht ODER beides)
  const savePreferences = useCallback(async (preferencesToSave) => {
      // Erwartet ein Objekt wie { tableColumns: [...] } oder { preferredView: '...' }
      setIsSavingPreferences(true);
      setError(null);
      try {
          const updatedProfile = await updateUserProfileAPI(preferencesToSave);
          // Aktualisiere den lokalen State basierend auf der Antwort des Backends
          if (updatedProfile?.preferredView) {
              setPreferredView(updatedProfile.preferredView);
          }
          if (updatedProfile?.tableColumns) {
               const columnsWithLabels = updatedProfile.tableColumns
                .map(colId => allColumnsConfig.find(col => col.id === colId))
                .filter(col => col);
              setColumns(columnsWithLabels);
          }
          setUserProfile(updatedProfile); // Aktualisiere das gesamte Profilobjekt
          return true; // Erfolg signalisieren
      } catch (err) {
          handleApiError(err);
          setError(err.message);
          return false; // Misserfolg signalisieren
      } finally {
          setIsSavingPreferences(false);
      }
  // allColumnsConfig ist konstant und muss nicht in die Abhängigkeiten
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleApiError]);

  // Lade Profil beim Initialisieren
  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Rückgabewerte des Hooks
  return {
      columns,
      userProfile,
      preferredView,
      allColumnsConfig,
      isLoadingProfile,     // Nur noch dieser Ladezustand für's Laden
      isSavingPreferences,  // Nur noch dieser Ladezustand für's Speichern
      error,
      fetchProfile,         // Nur noch diese Funktion zum Laden
      savePreferences     // Nur noch diese Funktion zum Speichern
  };
};
